import { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';

import TagsCell from '../../../components/DataTable/TagsCell/TagsCell';
import { iTag, Tag } from '../../../core/models/Tag';
import { NewsArticleModel } from '../../../core/apiSchema';
import SortableHeader from '../../../components/DataTable/SortTableHeader/SortTableHeader';
import { getDescriptionSnippet } from '../../../components/DataTable/DataTable.helpers';
import { highlightSearchText, prepareForMatching } from '../../../core/helpers';

export type SortKey = 'title' | 'description' | 'publishedDate';

interface UseGetTableColumnsParams {
    orderBy: SortKey;
    sortOrder: 'asc' | 'desc';
    onSort: (columnKey: string, nextOrder: string) => void;
    expandedRows: Set<number>;
    toggleRowExpansion: (id: number) => void;
    searchQuery?: string;
    onTagDrillDown?: (tag: iTag) => void;
    tagsLookupMap?: Map<string, iTag>;
}

export default function useGetTableColumns({
    orderBy,
    sortOrder,
    onSort,
    expandedRows,
    toggleRowExpansion,
    searchQuery,
    onTagDrillDown,
    tagsLookupMap
}: Readonly<UseGetTableColumnsParams>) {
    const columns = useMemo<ColumnDef<NewsArticleModel>[]>(() => {
        return [
            {
                id: 'cover',
                cell: (info) => {
                    const item = info.row.original;
                    const src = item.featuredImage || '';
                    const alt = item.title || '';
                    return (
                        <div className="Table__cover">
                            <img className="Table__cover-img" src={src} alt={alt} />
                        </div>
                    );
                },
                size: 120
            },
            {
                accessorKey: 'title',
                header: () => (
                    <SortableHeader columnKey="title" orderBy={orderBy} sortOrder={sortOrder} onSort={onSort} defaultOrder="asc">
                        HEADLINE
                    </SortableHeader>
                ),
                cell: (info) => {
                    const title = info.getValue() as string | undefined;

                    if (!title) return <div className="Table__title">-</div>;

                    if (searchQuery && title.toLowerCase().includes(searchQuery.toLowerCase())) {
                        const { snippet, prefix, suffix } = getDescriptionSnippet(title, searchQuery);
                        return (
                            <div className="Table__title" title={title}>
                                {prefix}
                                {highlightSearchText(snippet, searchQuery)}
                                {suffix}
                            </div>
                        );
                    }

                    return (
                        <span className="Table__title" title={title}>
                            {title}
                        </span>
                    );
                },
                size: 280
            },
            {
                accessorKey: 'publishedDate',
                header: () => (
                    <SortableHeader
                        columnKey="publishedDate"
                        orderBy={orderBy}
                        sortOrder={sortOrder}
                        onSort={onSort}
                        defaultOrder="desc"
                    >
                        <>
                            <span className="Table__header-date-top">DATE</span>
                            <span className="Table__header-date-bottom">PUBLISHED</span>
                        </>
                    </SortableHeader>
                ),
                cell: (info) => {
                    const date = info.getValue() as string | undefined;
                    if (!date) return '-';
                    const formatted = new Date(date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    });
                    return <span>{formatted}</span>;
                },
                size: 120
            },
            {
                accessorKey: 'description',
                header: () => (
                    <SortableHeader
                        columnKey="description"
                        orderBy={orderBy}
                        sortOrder={sortOrder}
                        onSort={onSort}
                        defaultOrder="asc"
                    >
                        DESCRIPTION
                    </SortableHeader>
                ),
                cell: (info) => {
                    const description = info.getValue() as string | undefined;
                    if (!description) return <div className="Table__description">-</div>;

                    if (searchQuery && description.toLowerCase().includes(searchQuery.toLowerCase())) {
                        const { snippet, prefix, suffix } = getDescriptionSnippet(description, searchQuery);
                        return (
                            <div className="Table__description Table__description--expanded" title={description}>
                                {prefix}
                                {highlightSearchText(snippet, searchQuery)}
                                {suffix}
                            </div>
                        );
                    }

                    return (
                        <div className="Table__description" title={description}>
                            {description}
                        </div>
                    );
                },
                size: 360
            },
            {
                accessorKey: 'tags',
                header: () => (
                    <div className="Table__tags-header">
                        <span>TAGS</span>
                        <small className="Table__tags-header-small">CLICK TO DRILL DOWN</small>
                    </div>
                ),
                cell: (info) => {
                    const newsArticle = info?.row?.original;
                    const rowId = newsArticle?.id ?? 0;

                    if (!rowId || !newsArticle?.tags?.length) return null;

                    const tags = newsArticle?.tags
                        .map((tagTitle) => {
                            const matchedTag = tagsLookupMap?.get(prepareForMatching(tagTitle));
                            if (!matchedTag?.id) return null;
                            return new Tag({ apiModel: { id: matchedTag.id, value: tagTitle } });
                        })
                        .filter((tag): tag is Tag => tag !== null);

                    if (!tags.length) return null;

                    const isExpanded = expandedRows.has(rowId);

                    return (
                        <TagsCell
                            rowId={rowId}
                            tags={tags}
                            isExpanded={isExpanded}
                            toggleRowExpansion={toggleRowExpansion}
                            onTagClick={onTagDrillDown}
                        />
                    );
                },
                size: 320
            }
        ];
    }, [orderBy, sortOrder, onSort, searchQuery, expandedRows, toggleRowExpansion, onTagDrillDown, tagsLookupMap]);

    return columns;
}
